import type { MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Flask } from '@phosphor-icons/react';
import { site } from '../../content/site';
import { useLenis } from '../../lib/SmoothScroll';
import { scrollToId } from '../../lib/scroll';

const LINK_BASE =
	'group inline-flex min-h-11 items-center gap-1.5 text-mineral/80 transition-colors duration-300 ease-expo hover:text-mineral';
const LINK = `${LINK_BASE} text-[15px]`;

function ExternalMark() {
	return (
		<ArrowUpRight
			aria-hidden
			size={14}
			weight="bold"
			className="text-dusk-muted transition-transform duration-300 ease-expo group-hover:-translate-y-px group-hover:translate-x-px"
		/>
	);
}

/** Split the tagline into its sentences so each one gets its own line. */
function sentences(text: string): string[] {
	return text
		.replace(/([.!?])\s+/g, '$1\n')
		.split('\n')
		.filter(Boolean);
}

/**
 * Footer, always on Forest Night (it paints its own background, so a direct load
 * never shows it on the day canvas). Ends with a giant cropped wordmark.
 */
export function Footer() {
	const lenis = useLenis();
	const year = new Date().getFullYear();
	const { person, nav, footer } = site;

	const onSection = (event: MouseEvent<HTMLAnchorElement>, id: string) => {
		event.preventDefault();
		scrollToId(lenis, id);
	};

	return (
		<footer className="relative z-10 bg-forest-night text-mineral">
			{/* Soft edge: the fixed meadow behind Contact fades into the footer instead of being cut. */}
			<div
				aria-hidden
				className="pointer-events-none absolute inset-x-0 bottom-full h-[clamp(4rem,9vw,8rem)] bg-gradient-to-b from-forest-night/0 to-forest-night"
			/>
			<div className="container-site pt-[clamp(3rem,6vw,5rem)]">
				<div className="grid gap-y-12 border-t border-mineral/10 pt-14 md:grid-cols-12 md:gap-x-6 md:pt-16">
					<p className="font-display text-[clamp(1.875rem,3.4vw,3.25rem)] font-bold leading-[1.02] tracking-[-0.03em] text-mineral md:col-span-7 lg:col-span-6">
						{sentences(footer.tagline).map((sentence) => (
							<span key={sentence} className="block">
								{sentence}
							</span>
						))}
					</p>

					<nav
						aria-label={footer.navLabel}
						className="grid grid-cols-2 gap-x-6 md:col-span-5 md:col-start-8 lg:col-span-4 lg:col-start-9"
					>
						<ul>
							{nav.links.map((link) => (
								<li key={link.id}>
									<a href={`#${link.id}`} onClick={(event) => onSection(event, link.id)} className={LINK}>
										{link.label}
									</a>
								</li>
							))}
						</ul>
						<ul>
							<li>
								<Link to="/globe" className={LINK}>
									{footer.globe}
								</Link>
							</li>
							<li>
								<a href={person.github} target="_blank" rel="noopener noreferrer" className={LINK}>
									{footer.github}
									<ExternalMark />
								</a>
							</li>
						</ul>
					</nav>
				</div>

				<a
					href={person.lab.href}
					target="_blank"
					rel="noopener noreferrer"
					className="group mt-14 inline-flex min-h-11 items-center gap-2.5 text-sm text-dusk-muted transition-colors duration-300 ease-expo hover:text-mineral md:mt-20"
				>
					<Flask aria-hidden size={18} weight="regular" className="shrink-0" />
					<span>
						<span className="font-medium text-mineral/80 group-hover:text-mineral">{footer.lab}</span>
						<span aria-hidden>{' '}</span>
						{footer.labText}
					</span>
					<ArrowUpRight
						aria-hidden
						size={14}
						weight="bold"
						className="shrink-0 transition-transform duration-300 ease-expo group-hover:-translate-y-px group-hover:translate-x-px"
					/>
				</a>

				<div className="mt-2 grid gap-2 text-sm text-dusk-muted sm:flex sm:items-center sm:justify-between md:mt-20">
					<p>
						© {year} {person.name}. {footer.rights}
					</p>
					<a href={`mailto:${person.email}`} className={`${LINK_BASE} text-sm`}>
						{person.email}
					</a>
				</div>
			</div>

			{/* Giant wordmark, cropped by the bottom edge of the page. "Ozan" is about 2.3em
			    wide, so 34rem keeps it inside the 1400px container on wide screens. */}
			<div
				aria-hidden
				className="pointer-events-none mt-6 select-none overflow-hidden text-[clamp(6rem,38vw,34rem)] leading-none"
			>
				<p className="container-site h-[0.62em] whitespace-nowrap font-display font-extrabold leading-[0.82] tracking-[-0.05em] text-mineral/[0.07]">
					{person.name}
				</p>
			</div>
		</footer>
	);
}
